/**
 * Add Block Dialog
 * 
 * Dialog component for adding a new block.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Search } from "lucide-react";
import { blockService } from "@/services/blockService";
import { toast } from "sonner";

interface AddBlockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddBlockDialog({
  open,
  onOpenChange,
  onSuccess,
}: AddBlockDialogProps) {
  const [callsign, setCallsign] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [validating, setValidating] = React.useState(false);
  const [previewData, setPreviewData] = React.useState<{
    id_client: string;
    nama: string;
    province: string;
    kabkot: string;
  } | null>(null);

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setCallsign("");
      setReason("");
      setError(null);
      setPreviewData(null);
    }
  }, [open]);

  // Validate callsign via SDPPI
  const handleValidateCallsign = async () => {
    if (!callsign.trim()) {
      setError("Callsign harus diisi");
      return;
    }

    setValidating(true);
    setError(null);
    setPreviewData(null);

    try {
      const result = await blockService.validateCallsign(callsign.trim().toUpperCase());

      if (result.success && result.data) {
        setPreviewData(result.data);
        toast.success("Callsign ditemukan di sistem SDPPI", {
          description: `User: ${result.data.nama}`,
        });
      } else {
        setError(result.error || "Callsign tidak ditemukan di sistem SDPPI");
        setPreviewData(null);
      }
    } catch (err) {
      console.error("Error validating callsign:", err);
      const errorMessage = err instanceof Error ? err.message : "Gagal memvalidasi callsign";
      setError(errorMessage);
      setPreviewData(null);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate input - callsign must be provided
      if (!callsign.trim()) {
        setError("Callsign harus diisi");
        setLoading(false);
        return;
      }

      // Call API to block user (backend will fetch data from SDPPI)
      const result = await blockService.blockUser({
        callsign: callsign.trim().toUpperCase(),
        reason: reason.trim() || undefined,
      });

      if (result.success) {
        toast.success("User berhasil di-block", {
          description: `User ${result.block?.callsign} telah di-block`,
        });
        onOpenChange(false);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(result.message);
        toast.error("Gagal mem-block user", {
          description: result.message,
        });
      }
    } catch (err) {
      console.error("Error blocking user:", err);
      const errorMessage = err instanceof Error ? err.message : "Terjadi kesalahan saat mem-block user";
      setError(errorMessage);
      toast.error("Gagal mem-block user", {
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Block User</DialogTitle>
          <DialogDescription>
            Tambahkan user baru ke daftar block. Masukkan Callsign untuk mem-block user. Data user akan diambil dari sistem SDPPI.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Callsign Input */}
          <div className="space-y-2">
            <Label htmlFor="callsign-input">
              Callsign <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="callsign-input"
                placeholder="Masukkan Callsign"
                value={callsign}
                onChange={(e) => {
                  setCallsign(e.target.value.toUpperCase());
                  setPreviewData(null);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && callsign.trim()) {
                    e.preventDefault();
                    handleValidateCallsign();
                  }
                }}
                disabled={loading || validating}
                className="pr-10"
              />
              {validating && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!validating && callsign.trim() && (
                <button
                  type="button"
                  onClick={handleValidateCallsign}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  disabled={loading}
                >
                  <Search className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Tekan Enter atau klik tombol search untuk memvalidasi callsign di sistem SDPPI
            </p>
          </div>

          {/* Preview Data from SDPPI */}
          {previewData && (
            <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
              <p className="text-sm font-semibold">Data dari SDPPI:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">ID Client:</span>
                  <p className="font-mono font-medium">{previewData.id_client || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Nama:</span>
                  <p className="font-medium">{previewData.nama || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Provinsi:</span>
                  <p className="font-medium">{previewData.province || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Kabupaten/Kota:</span>
                  <p className="font-medium">{previewData.kabkot || "-"}</p>
                </div>
              </div>
            </div>
          )}

          {/* Reason Input */}
          <div className="space-y-2">
            <Label htmlFor="reason-input" className="text-sm">Alasan (Opsional)</Label>
            <Textarea
              id="reason-input"
              placeholder="Masukkan alasan mengapa user ini di-block"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              rows={2}
              className="text-sm"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Batal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              loading ||
              !callsign.trim()
            }
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Memproses...
              </>
            ) : (
              "Block User"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

